export const AI_INTERVIEW_INVITATION_IDEMPOTENCY_PREFIX = 'forkhire.ai_interview.invitation:';
export const MANUAL_AI_INTERVIEW_INVITATION_IDEMPOTENCY_PREFIX = 'forkhire.ai_interview.manual-resend:';

type SendEmailInvokeResult = {
  data: unknown;
  error: unknown;
};

type SendEmailInvoker = (
  functionName: string,
  options: { body: Record<string, unknown> },
) => Promise<SendEmailInvokeResult>;

type SendManualAiInterviewInvitationArgs = {
  sessionId: string;
  resendActionId: string;
  body: Record<string, unknown>;
  invoke: SendEmailInvoker;
};

type SendEmailSuccessPayload = {
  success?: unknown;
  status?: unknown;
  idempotent_replay?: unknown;
};

export function aiInterviewInvitationIdempotencyKey(sessionId: string): string {
  const normalizedSessionId = sessionId.trim();
  if (!normalizedSessionId) throw new Error('AI Interview session identity is required');
  return `${AI_INTERVIEW_INVITATION_IDEMPOTENCY_PREFIX}${normalizedSessionId}`;
}

export function manualAiInterviewInvitationIdempotencyKey(
  sessionId: string,
  resendActionId: string,
): string {
  const normalizedSessionId = sessionId.trim();
  const normalizedActionId = resendActionId.trim();
  if (!normalizedSessionId) throw new Error('AI Interview session identity is required');
  if (!normalizedActionId) throw new Error('Manual resend action identity is required');
  return `${MANUAL_AI_INTERVIEW_INVITATION_IDEMPOTENCY_PREFIX}${normalizedSessionId}:${normalizedActionId}`;
}

export function aiInterviewCandidateNotificationIdentity(sessionId: string): {
  interview_session_id: string;
} {
  const normalizedSessionId = sessionId.trim();
  if (!normalizedSessionId) throw new Error('AI Interview session identity is required');
  return { interview_session_id: normalizedSessionId };
}

export function isSuccessfulAiInterviewInvitationDelivery(
  result: SendEmailInvokeResult,
): boolean {
  if (result.error) return false;
  const payload = result.data as SendEmailSuccessPayload | null;
  return payload?.success === true && payload.status === 'sent';
}

export async function sendManualAiInterviewInvitation({
  sessionId,
  resendActionId,
  body,
  invoke,
}: SendManualAiInterviewInvitationArgs): Promise<{
  requestCreated: boolean;
  providerAccepted: boolean;
  deliveryGuaranteed: false;
  idempotencyKey: string;
  response: SendEmailInvokeResult;
}> {
  const idempotencyKey = manualAiInterviewInvitationIdempotencyKey(sessionId, resendActionId);
  const response = await invoke('send-email', {
    body: {
      ...body,
      email_idempotency_key: idempotencyKey,
    },
  });

  return {
    requestCreated: !response.error,
    providerAccepted: isSuccessfulAiInterviewInvitationDelivery(response),
    deliveryGuaranteed: false,
    idempotencyKey,
    response,
  };
}
